import { toast } from 'sonner';
import { X, AlertTriangle } from 'lucide-react';
import { playAlertSound, playWarningSound } from '@/client/lib/sound';

interface FlappingToastProps {
  toastId: string | number;
  encoderId: string;
  simpleName: string;
  cycles: number;
  severity: 'warning' | 'alert';
  /** True if this is a live stream disconnect (not flapping cycles) */
  isLiveStreamDisconnect?: boolean;
}

function FlappingToastContent({ toastId, encoderId, simpleName, cycles, severity, isLiveStreamDisconnect }: FlappingToastProps) {
  const isAlert = severity === 'alert';

  // Live stream disconnect uses a distinct blue/info style
  const colorClasses = isLiveStreamDisconnect
    ? 'bg-blue-100 border-blue-300 text-blue-900'
    : isAlert
      ? 'bg-red-100 border-red-300 text-red-900'
      : 'bg-amber-100 border-amber-300 text-amber-900';

  const iconColor = isLiveStreamDisconnect
    ? 'text-blue-600'
    : isAlert ? 'text-red-600' : 'text-amber-600';

  const buttonColor = isLiveStreamDisconnect
    ? 'text-blue-700'
    : isAlert ? 'text-red-700' : 'text-amber-700';

  const descColor = isLiveStreamDisconnect
    ? 'text-blue-800'
    : isAlert ? 'text-red-800' : 'text-amber-800';

  // Title and description based on type
  const title = isLiveStreamDisconnect
    ? 'Stream Disconnected'
    : isAlert ? 'Encoder Flapping' : 'Encoder Warning';

  const description = isLiveStreamDisconnect
    ? 'Encoder went offline during live stream'
    : `${cycles} disconnect/reconnect cycle${cycles !== 1 ? 's' : ''} detected`;

  return (
    <div
      className={`
        flex flex-col gap-2 p-4 rounded-lg shadow-lg border min-w-[320px]
        ${colorClasses}
      `}
    >
      {/* Header with close button */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <AlertTriangle className={`h-5 w-5 flex-shrink-0 ${iconColor}`} />
          <div>
            <div className="font-semibold text-sm">
              {title}
            </div>
            <div className="font-medium">
              {simpleName || encoderId}
            </div>
          </div>
        </div>
        <button
          onClick={() => toast.dismiss(toastId)}
          className={`
            p-1 rounded hover:bg-black/10 transition-colors
            ${buttonColor}
          `}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Description */}
      <div className={`text-sm ${descColor}`}>
        {description}
      </div>

      {/* Action links - using <a> tags because toast renders outside router context */}
      <div className="flex items-center gap-3 text-sm font-medium mt-1">
        <a
          href="/encoders/triage"
          onClick={() => setTimeout(() => toast.dismiss(toastId), 0)}
          className={`hover:underline ${buttonColor}`}
        >
          Triage Page
        </a>
        <span className={isLiveStreamDisconnect ? 'text-blue-400' : isAlert ? 'text-red-400' : 'text-amber-400'}>|</span>
        <a
          href={`/encoders/${encoderId}`}
          onClick={() => setTimeout(() => toast.dismiss(toastId), 0)}
          className={`hover:underline ${buttonColor}`}
        >
          Encoder Details
        </a>
      </div>
    </div>
  );
}

/**
 * Show a persistent flapping toast notification
 * Stacks in bottom-right, requires manual dismissal
 */
export function showFlappingToast(props: Omit<FlappingToastProps, 'toastId'>) {
  const toastId = `flapping-${props.encoderId}-${Date.now()}`;

  // Play sound based on severity
  if (props.severity === 'alert') {
    playAlertSound();
  } else {
    playWarningSound();
  }

  toast.custom(
    (id) => (
      <FlappingToastContent
        {...props}
        toastId={id}
      />
    ),
    {
      id: toastId,
      duration: Infinity,
      position: 'bottom-right',
    }
  );

  return toastId;
}

/**
 * Dismiss a specific flapping toast
 */
export function dismissFlappingToast(toastId: string | number) {
  toast.dismiss(toastId);
}

/**
 * Dismiss all flapping toasts for an encoder (when resolved)
 */
export function dismissFlappingToastsForEncoder(encoderId: string) {
  // Note: Sonner doesn't provide a way to query active toasts by pattern
  // We'd need to track toast IDs externally if we want this functionality
  // For now, resolved encoders will have their toasts dismissed when user clicks X
}
